import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import InputText from "components/atoms/InputText";
import Button from "components/atoms/Button";
import Icon from "components/atoms/Icon";
import classnames from "classnames";
import makePersistMountedState from "hoc/mountedState";
import useSuggestionDropdown from "hooks/useSuggestionDropdown";
import useClickOutside from "hooks/useClickOutside";
import useDebounce from "hooks/useDebounce";
import SuggestionsList from "./SuggestionsList";

const InputSearch = ({
  name,
  onSearch,
  placeholder,
  className,
  isFormSubmit,
  onSuggestionsRequest,
  suggestions,
  value = "",
  removeInitialValue = false,
  allowEmptySearch = false,
}) => {
  const [currentValue, setCurrentValue] = useState("");
  const [suggestedValue, setSuggestedValue] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const currentValueFromProps = useRef(true);
  const previousSearchValue = useRef("");
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debouncedSuggestionRequest = onSuggestionsRequest
    ? useDebounce((value) => onSuggestionsRequest(value), 150)
    : null;

  const submitSearch = (term) => {
    if (term || (term === "" && allowEmptySearch)) {
      setSearchValue(term);
    }
  };

  const [
    eventHandlers = {},
    suggestionsOpen,
    suggestIndex,
    dropdownActions = {},
  ] =
    suggestions && suggestions.terms
      ? useSuggestionDropdown(suggestions.terms, currentValue, {
          onEnter: () => submitSearch(suggestedValue),
        })
      : [];

  useClickOutside(containerRef, eventHandlers.onBlur);

  const handleInputChange = (e) => {
    currentValueFromProps.current = false;
    setSuggestedValue(e.target.value);
    setCurrentValue(e.target.value);
  };

  const handleButtonSubmit = (e) => {
    e.preventDefault();
    submitSearch(currentValue);
  };

  const handleSuggestionSearch = (term) => {
    submitSearch(term);
  };
  const handleSuggestionSelect = (term) => {
    setCurrentValue(term);

    if (inputRef.current !== null) {
      inputRef.current.focus();
    }
  };

  const handleResetButton = () => {
    setCurrentValue("");
    if (allowEmptySearch) {
      setSearchValue("");
    }
  };

  useEffect(() => {
    currentValueFromProps.current = true;
    setCurrentValue(value);
  }, [value]);

  useEffect(() => {
    if (debouncedSuggestionRequest && !currentValueFromProps.current) {
      debouncedSuggestionRequest(currentValue);
    }
    if (currentValue !== suggestedValue) {
      setSuggestedValue(currentValue);
      if (removeInitialValue) {
        setSuggestedValue("");
        removeInitialValue = false;
      }
    }
  }, [currentValue]);

  useEffect(() => {
    if (suggestIndex === "undefined" || suggestIndex === -1) {
      setSuggestedValue(currentValue);
      return;
    }
    if (suggestions && suggestions.terms && suggestions.terms[suggestIndex]) {
      setSuggestedValue(suggestions.terms[suggestIndex]);
    }
  }, [suggestIndex]);

  useEffect(() => {
    if (searchValue || (searchValue === "" && allowEmptySearch)) {
      // For allowEmptySearch mode, only trigger on value changes
      if (allowEmptySearch && previousSearchValue.current === searchValue) {
        previousSearchValue.current = searchValue;
        return;
      }

      onSearch(
        searchValue || "",
        searchValue !== currentValue ? currentValue : "",
      );

      if (!isFormSubmit) {
        setSuggestedValue(searchValue);
        setCurrentValue(searchValue);
        if (dropdownActions.close) {
          dropdownActions.close();
        }
      }
      previousSearchValue.current = searchValue;
    }
  }, [searchValue]);

  const componentBaseClass = "sal-input-search";
  const componentClass = classnames(componentBaseClass, className, {
    "has-value": suggestedValue && suggestedValue !== "",
  });

  return (
    <div className={componentClass} ref={containerRef}>
      <label htmlFor="input-search" className={componentBaseClass + "__label "}>
        {placeholder}
      </label>
      <InputText
        value={suggestedValue}
        placeholder={placeholder}
        type="text"
        onChange={handleInputChange}
        onKeyDown={eventHandlers.onKeyDown}
        onFocus={eventHandlers.onFocus}
        className={componentBaseClass + "__input"}
        ref={inputRef}
        id="input-search"
        name="input-search"
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck="false"
      />
      <input type="hidden" name={name} value={searchValue} />
      <Button
        name={`${name}-btn-clear`}
        type="reset"
        aria-label="Eingabe löschen"
        onClick={handleResetButton}
        className={
          componentBaseClass + "__reset " + componentBaseClass + "__button"
        }
      >
        <Icon
          type="Schliessen"
          className={componentBaseClass + "__reset-icon"}
          aria-hidden="true"
        />
      </Button>
      <Button
        name={`${name}-btn-submit`}
        type="submit"
        onClick={handleButtonSubmit}
        className={
          componentBaseClass + "__submit " + componentBaseClass + "__button"
        }
        aria-label="Suchen"
      >
        <Icon
          type="Suchen"
          className={componentBaseClass + "__search-icon"}
          aria-hidden="true"
        />
      </Button>
      <SuggestionsList
        className={componentBaseClass + "__suggest-box"}
        currentTerm={currentValue}
        open={suggestionsOpen}
        suggestions={suggestions ? suggestions.terms : []}
        onTermSearch={handleSuggestionSearch}
        onTermSelect={handleSuggestionSelect}
        selectedIndex={suggestIndex}
      />
    </div>
  );
};

InputSearch.propTypes = {
  name: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  isFormSubmit: PropTypes.bool,
  onSuggestionsRequest: PropTypes.func,
  suggestions: PropTypes.shape({
    loading: PropTypes.bool,
    terms: PropTypes.array,
  }),
  value: PropTypes.string,
  removeInitialValue: PropTypes.bool,
  allowEmptySearch: PropTypes.bool,
};

export default makePersistMountedState(InputSearch);
